import React, { useState } from "react";
import { Alert, Button, Card, Input, Select, Space, Tag, Typography } from "antd";
import {
  agentPlatformApi, AgentPlatformApiError, ReproductionPreview,
  ReproductionRequestPayload, SupportCase,
} from "./api";

const { Text } = Typography;
type Draft = Omit<ReproductionRequestPayload, "expectedCaseRevision">;
const initialDraft = (supportCase: SupportCase): Draft => {
  const proposed = supportCase.development?.reproductionRequests?.[0];
  return {
    operation: proposed?.operation || "translation",
    bookId: proposed?.bookId || "", chapterId: proposed?.chapterId || "",
    sourceLang: proposed?.sourceLang || "", targetLangs: proposed?.targetLangs || [],
    model: "", repeats: proposed?.repeats || 1, paragraphIndex: proposed?.paragraphIndex ?? 0,
  };
};
const clean = (value: string) => value.trim();
const valid = (draft: Draft) =>
  (draft.operation !== "proofreading" || (draft.targetLangs.length === 1 && draft.targetLangs[0] === "Original" && draft.repeats === 1 && draft.paragraphIndex === 0)) &&
  Boolean(clean(draft.bookId) && clean(draft.chapterId) && clean(draft.sourceLang) && clean(draft.model)) &&
  draft.targetLangs.length >= 1 && draft.targetLangs.length <= 3 &&
  draft.targetLangs.every((value) => Boolean(clean(value))) &&
  new Set(draft.targetLangs).size === draft.targetLangs.length &&
  Number.isInteger(draft.repeats) && draft.repeats >= 1 && draft.repeats <= 4 &&
  Number.isInteger(draft.paragraphIndex) && draft.paragraphIndex >= 0 && draft.paragraphIndex <= 100;

export default function SupportReproductionReview({ workspaceId, supportCase, onApproved }: {
  workspaceId: string; supportCase: SupportCase; onApproved: () => void;
}): React.ReactElement {
  const [draft, setDraft] = useState<Draft>(() => initialDraft(supportCase));
  const [requestIndex, setRequestIndex] = useState(0);
  const [targetsText, setTargetsText] = useState(() => initialDraft(supportCase).targetLangs.join(", "));
  const [preview, setPreview] = useState<{ result: ReproductionPreview; request: ReproductionRequestPayload }>();
  const [pending, setPending] = useState<"preview" | "approve">();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const requests = supportCase.development?.reproductionRequests || [];
  const payload: ReproductionRequestPayload = {
    ...(draft.operation === "proofreading" ? { operation: "proofreading" as const } : {}),
    bookId: clean(draft.bookId), chapterId: clean(draft.chapterId),
    sourceLang: clean(draft.sourceLang), targetLangs: draft.targetLangs.map(clean),
    model: clean(draft.model), repeats: draft.repeats, paragraphIndex: draft.paragraphIndex,
    expectedCaseRevision: supportCase.revision,
  };
  const matchesPreview = preview && JSON.stringify(payload) === JSON.stringify(preview.request);
  const setField = <K extends keyof Draft>(name: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setPreview(undefined); setError(undefined); setNotice(undefined);
  };
  const selectProposal = (index: number) => {
    const selected = requests[index];
    if (!selected) return;
    setRequestIndex(index);
    setTargetsText((selected.targetLangs || []).join(", "));
    setDraft({ operation: selected.operation || "translation", bookId: selected.bookId || "", chapterId: selected.chapterId || "",
      sourceLang: selected.sourceLang || "", targetLangs: selected.targetLangs || [],
      repeats: selected.repeats || 1, model: "", paragraphIndex: selected.paragraphIndex ?? 0 });
    setPreview(undefined); setError(undefined); setNotice(undefined);
  };
  const previewPlan = async () => {
    if (!valid(draft) || pending) return;
    setPending("preview"); setPreview(undefined); setError(undefined); setNotice(undefined);
    try {
      const result = await agentPlatformApi.previewSupportReproduction(workspaceId, supportCase.id, payload);
      setPreview({ result, request: payload });
    } catch (error) {
      setError(error instanceof AgentPlatformApiError && error.status === 409
        ? "The case or source changed. Refresh the case before previewing again."
        : error instanceof AgentPlatformApiError && error.status === 503
        ? "Local reproduction capability is unavailable. Check the local runtime and its DEV configuration before retrying."
        : "The reproduction preview is unavailable. Check the exact scope and try again.");
    } finally { setPending(undefined); }
  };
  const approve = async () => {
    if (!matchesPreview || !preview || pending) return;
    setPending("approve"); setError(undefined);
    try {
      await agentPlatformApi.approveSupportReproduction(workspaceId, supportCase.id,
        { ...preview.request, planHash: preview.result.planHash });
      setPreview(undefined);
      setNotice("Exact DEV reproduction approved. Check Engineering for progress.");
      onApproved();
    } catch (error) {
      setPreview(undefined);
      setError(error instanceof AgentPlatformApiError && error.status === 409
        ? "The exact plan changed. Refresh the case and preview again."
        : error instanceof AgentPlatformApiError && error.status === 503
        ? "Local reproduction capability is unavailable. Check the local runtime before retrying."
        : "Approval did not complete. Refresh the case before trying again.");
    } finally { setPending(undefined); }
  };
  return <Card size="small" title="Controlled DEV reproduction">
    <Space direction="vertical" size={8} className="agent-platform-full">
      <Text type="secondary">Proposals are unexecuted. Enter the exact model and verify the book and chapter before preview.</Text>
      {requests.length > 1 && <Select aria-label="Reproduction proposal" value={requestIndex}
        onChange={selectProposal} options={requests.map((_, index) => ({
          value: index, label: `Proposal ${index + 1}`,
        }))} />}
      {requests[requestIndex]?.reason && <Text>Proposed reason: {requests[requestIndex].reason}</Text>}
      <Select aria-label="Reproduction operation" value={draft.operation || "translation"}
        options={[{ value: "translation", label: "Translation" }, { value: "proofreading", label: "Proofreading (Original)" }]}
        onChange={(operation: "translation" | "proofreading") => {
          setDraft((current) => ({ ...current, operation, targetLangs: operation === "proofreading" ? ["Original"] : [], repeats: 1, paragraphIndex: 0 }));
          setTargetsText(operation === "proofreading" ? "Original" : "");
          setPreview(undefined); setError(undefined); setNotice(undefined);
        }} />
      {draft.operation === "proofreading" && <Text>One Original-language proofreading review on an admin copy. Changes are not automatically applied.</Text>}
      <Input aria-label="Reproduction book ID" placeholder="Exact book ID" value={draft.bookId}
        maxLength={160} onChange={(event) => setField("bookId", event.target.value)} />
      <Input aria-label="Reproduction chapter ID" placeholder="Exact chapter ID" value={draft.chapterId}
        maxLength={160} onChange={(event) => setField("chapterId", event.target.value)} />
      <Input aria-label="Reproduction source language" placeholder="Source language" value={draft.sourceLang}
        maxLength={80} onChange={(event) => setField("sourceLang", event.target.value)} />
      <Input aria-label="Reproduction target languages" placeholder="Target languages, comma separated"
        disabled={draft.operation === "proofreading"} value={targetsText} onChange={(event) => {
          setTargetsText(event.target.value);
          setField("targetLangs", event.target.value.split(",").map(clean).filter(Boolean));
        }} />
      <Input aria-label="Reproduction model" placeholder="Exact model ID" value={draft.model}
        maxLength={120} onChange={(event) => setField("model", event.target.value)} />
      <Space wrap>
        <label>Repeats <Input type="number" aria-label="Reproduction repeats" min={1} max={4}
          disabled={draft.operation === "proofreading"} value={draft.repeats} onChange={(event) => setField("repeats", Number(event.target.value))} /></label>
        <label>Paragraph index <Input type="number" aria-label="Reproduction paragraph index" min={0} max={100}
          disabled={draft.operation === "proofreading"} value={draft.paragraphIndex} onChange={(event) => setField("paragraphIndex", Number(event.target.value))} /></label>
      </Space>
      <Button onClick={() => void previewPlan()} loading={pending === "preview"}
        disabled={!valid(draft) || Boolean(pending)}>Preview exact plan</Button>
      {matchesPreview && preview && <Alert type="warning" showIcon message="Review before approval"
        description={<Space direction="vertical" size={3}>
          <Text>DEV project: {preview.result.route.project}; worker: {preview.result.route.worker}; region: {preview.result.route.region}</Text>
          <Text>Model: {preview.result.model}; total runs: {preview.result.totalRuns}</Text>
          <Text>Source version: {preview.result.sourceVersion}</Text>
          {preview.result.effects.map((effect, index) => <Text key={`effect-${index}`}>{effect}</Text>)}
          {preview.result.limitations.map((limitation, index) => <Text type="secondary" key={`limitation-${index}`}>{limitation}</Text>)}
          <Tag>Provider and admin-credit costs may apply</Tag>
        </Space>} />}
      {matchesPreview && <Button type="primary" danger onClick={() => void approve()}
        loading={pending === "approve"} disabled={Boolean(pending)}>Approve exact DEV plan</Button>}
      {error && <Alert type="error" showIcon message={error} />}
      {notice && <Alert type="success" showIcon message={notice} />}
    </Space>
  </Card>;
}
